import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useRecentReviews } from '../../../hooks/useRecentReviews';

// Premium glassmorphism review grid (2026-05-23). Apple/Vision-Pro frosted
// surfaces over a localized dark "stage" — the section above (stats row,
// testimonial video, heading) stays on white; we paint a soft dark
// radial gradient + cyan ambient blobs ONLY behind the review grid so
// the glass has something dark to blur against. Cards are 0.55-opaque
// dark-navy with backdrop-blur(24px) + saturate(180%) and a hairline
// white edge. One review (the longest, which is reliably the most
// substantive) gets featured treatment: 2×2 grid span on desktop, larger
// padding, lighter/larger quote, and a single tasteful accent — a giant
// faint cyan opening-quote glyph anchored behind the text. Everything
// else stays quiet.
//
// HIERARCHY decisions:
//   • Quote is the HERO — moved to the top of the card, larger size,
//     lighter weight, generous leading. Attribution row (avatar + name
//     + date + stars) is demoted to a quiet bottom strip.
//   • Featured card uses font-light + 2xl/3xl. Normal cards use
//     font-normal + base/lg. Hierarchy is set by SIZE and WEIGHT, not
//     by adding chrome.
//   • Stars kept gold (proof) but downsized 14→12px on normal cards,
//     14px on featured. Avatars downsized from 44→32px (normal) / 40px
//     (featured) so they frame the quote rather than compete with it.
//
// LAYOUT (cleaned up 2026-05-23): 5 cards in a 3-col / 2-row CSS grid on
// desktop where featured is col-span-2 (wide) at natural row height.
// Math: featured covers 2 cells, 4 normals fill remaining 4 cells = 6
// cells, all filled, no orphans.
//   ┌──────────┬─────┐
//   │ FEATURED │ R1  │
//   │  (×2)    │     │
//   ├────┬─────┴─────┤
//   │ R2 │ R3  │ R4  │
//   └────┴─────┴─────┘
// The previous version used row-span-2 on featured (3×3 = 9-cell grid,
// 6 cards). That forced featured to be 2× normal row height, creating
// a void of dead glass between its quote and attribution AND forcing
// R1/R2 on the right column to stretch to match — which produced the
// "middle-right floating gap" between them. Removing row-span-2 fixes
// both issues; the tradeoff is dropping from 6 reviews to 5.
//
// Tablet (sm, 2 cols): featured col-spans the full top row, 4 normals
// stack 2-up below.
// Mobile (1 col): featured first (reordered), then others stacked.
//
// MOBILE/BACKDROP-FILTER FALLBACK:
//   • base background at 0.55 alpha is readable WITHOUT the blur — if a
//     mobile browser doesn't support backdrop-filter, the card degrades
//     to a slightly translucent dark card on the dark backdrop stage
//     (still legible, no broken-transparent look).
//   • Both `backdrop-filter` and `-webkit-backdrop-filter` are set
//     inline for iOS Safari compatibility.

// 5 reviews (not 6) so the grid fits cleanly: featured spans 2 cells
// (col-span-2) + 4 normals = 6 cells = 3 cols × 2 rows, no orphan cells.
// With 6 reviews + col-span-2 featured we'd need 3×3 = 9 cells with
// 2 empty orphan cells visible on desktop — the 2026-05-23 cleanup pass
// chose to drop one card rather than ship the orphans.
const REVIEW_LIMIT = 5;
const SKELETON_COUNT = 5;
const STAGGER_STEP = 0.08;
const EASE_OUT_EXPO = [0.16, 1, 0.3, 1];

// ─── Glass card style tokens ────────────────────────────────────────────

const GLASS_BG = 'rgba(10, 22, 40, 0.55)';
const GLASS_BG_FEATURED = 'rgba(10, 22, 40, 0.50)';
const GLASS_BLUR = 'blur(24px) saturate(180%)';
const GLASS_BLUR_FEATURED = 'blur(28px) saturate(180%)';

const GLASS_BORDER = '1px solid rgba(255, 255, 255, 0.10)';
const GLASS_BORDER_FEATURED = '1px solid rgba(7, 176, 233, 0.35)';

const GLASS_SHADOW = [
  'inset 0 1px 0 rgba(255, 255, 255, 0.12)',
  '0 20px 40px rgba(10, 22, 40, 0.25)',
].join(', ');

const GLASS_SHADOW_FEATURED = [
  'inset 0 1px 0 rgba(255, 255, 255, 0.18)',
  '0 30px 60px rgba(7, 176, 233, 0.15)',
  '0 0 80px rgba(7, 176, 233, 0.08)',
].join(', ');

// ─── Subcomponents ──────────────────────────────────────────────────────

const StarRating = ({ rating, featured }) => {
  const size = featured ? 14 : 12;
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => {
        const filled = i < rating;
        return (
          <svg
            key={i}
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill={filled ? '#F59E0B' : 'none'}
            stroke={filled ? '#F59E0B' : 'rgba(255, 255, 255, 0.22)'}
            strokeWidth="1.5"
            style={filled ? { filter: 'drop-shadow(0 0 4px rgba(245, 158, 11, 0.55))' } : undefined}
            aria-hidden="true"
          >
            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
          </svg>
        );
      })}
    </div>
  );
};

const Avatar = ({ photoUrl, initials, featured }) => {
  // Quieter ring than the previous double-glow treatment — single subtle
  // border, no cyan halo, so the avatar frames the quote without competing.
  const dim = featured ? 'w-10 h-10' : 'w-8 h-8';
  const fontSize = featured ? 'text-sm' : 'text-xs';
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        className={`${dim} rounded-full object-cover flex-shrink-0`}
        style={{ boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.18)' }}
        referrerPolicy="no-referrer"
      />
    );
  }
  return (
    <div
      className={`${dim} ${fontSize} rounded-full flex items-center justify-center flex-shrink-0 text-white font-semibold`}
      style={{
        background: 'linear-gradient(135deg, rgba(7, 176, 233, 0.55) 0%, rgba(0, 102, 168, 0.85) 100%)',
        boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.15)',
      }}
    >
      {initials}
    </div>
  );
};

const ReviewCard = ({ review, index, featured }) => {
  const initials =
    review?.authorName
      ?.split(' ')
      ?.map((n) => n?.[0])
      ?.join('')
      ?.toUpperCase()
      ?.slice(0, 2) || '?';

  const dateLabel =
    review?.relativeTimeDescription ||
    (review?.time
      ? new Date(review.time * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
      : '');

  return (
    <motion.article
      initial={{ opacity: 0, y: featured ? 24 : 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      whileHover={{ y: -3 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{
        opacity: { duration: featured ? 0.7 : 0.55, delay: index * STAGGER_STEP, ease: EASE_OUT_EXPO },
        y: { duration: 0.3, ease: 'easeOut' },
      }}
      className={[
        'relative rounded-2xl overflow-hidden flex flex-col',
        // Featured is WIDE (col-span-2) but NOT row-span-2 anymore — that
        // was forcing it to occupy 2× normal row height, creating a void
        // of empty glass between the quote and the attribution. Now it
        // sits at natural content height, prominent via width + typography
        // alone. R1 in the same row also sits at its natural height,
        // eliminating the previous middle-right "floating gap" where R1
        // was being stretched to match featured's tall span.
        featured
          ? 'sm:col-span-2 lg:col-span-2 p-7 lg:p-8 gap-5 lg:gap-6'
          : 'p-6 lg:p-7 gap-5',
      ].join(' ')}
      style={{
        background: featured ? GLASS_BG_FEATURED : GLASS_BG,
        backdropFilter: featured ? GLASS_BLUR_FEATURED : GLASS_BLUR,
        WebkitBackdropFilter: featured ? GLASS_BLUR_FEATURED : GLASS_BLUR,
        border: featured ? GLASS_BORDER_FEATURED : GLASS_BORDER,
        boxShadow: featured ? GLASS_SHADOW_FEATURED : GLASS_SHADOW,
      }}
    >
      {/* RESTRAINED ACCENT — featured cards only: huge faint cyan
          opening-quote glyph anchored top-left, sits behind the quote
          text. Single accent for the whole grid. */}
      {featured && (
        <span
          aria-hidden="true"
          className="absolute top-0 left-4 lg:left-6 leading-none pointer-events-none select-none"
          style={{
            fontSize: 'clamp(120px, 14vw, 200px)',
            fontFamily: "'Poppins', 'Montserrat', serif",
            fontWeight: 900,
            color: 'rgba(7, 176, 233, 0.10)',
            transform: 'translateY(-0.15em)',
          }}
        >
          &ldquo;
        </span>
      )}

      {/* QUOTE — the hero. Sits on top of the decorative glyph (relative
          z-stacking via the article being relative + glyph being absolute,
          and the blockquote being normal flow which paints above). */}
      {review?.text && (
        <blockquote
          className={[
            'relative',
            featured
              ? 'text-2xl lg:text-3xl leading-[1.35] tracking-tight font-light'
              : 'text-base lg:text-[17px] leading-[1.6] font-normal line-clamp-5',
          ].join(' ')}
          style={{
            color: 'rgba(255, 255, 255, 0.94)',
            letterSpacing: featured ? '-0.01em' : '0',
          }}
        >
          {review.text}
        </blockquote>
      )}

      {/* Spacer pushes attribution to the bottom of the card so featured
          and normal cards have aligned attribution baselines. */}
      <div className="flex-1" />

      {/* ATTRIBUTION ROW — small, quiet, framing the quote. */}
      <div className="relative flex items-center gap-3">
        <Avatar photoUrl={review?.profilePhotoUrl} initials={initials} featured={featured} />
        <div className="flex-1 min-w-0">
          <p
            className={[
              featured ? 'text-[15px]' : 'text-sm',
              'font-medium truncate',
            ].join(' ')}
            style={{ color: 'rgba(255, 255, 255, 0.88)' }}
          >
            {review?.authorName}
          </p>
          <p
            className="text-[11px] tracking-wide truncate"
            style={{ color: 'rgba(255, 255, 255, 0.45)' }}
          >
            {dateLabel}
          </p>
        </div>
        <StarRating rating={review?.rating} featured={featured} />
      </div>
    </motion.article>
  );
};

const SkeletonCard = ({ featured }) => (
  <div
    className={[
      'rounded-2xl animate-pulse',
      featured ? 'sm:col-span-2 lg:col-span-2 h-44' : 'h-44',
    ].join(' ')}
    style={{
      background: featured ? GLASS_BG_FEATURED : GLASS_BG,
      backdropFilter: featured ? GLASS_BLUR_FEATURED : GLASS_BLUR,
      WebkitBackdropFilter: featured ? GLASS_BLUR_FEATURED : GLASS_BLUR,
      border: featured ? GLASS_BORDER_FEATURED : GLASS_BORDER,
    }}
  />
);

// ─── Helpers ────────────────────────────────────────────────────────────

// Pick the longest review text as the featured one. Length is a reliable
// proxy for "most substantive" — the brief specifically calls out the
// "Josh S … saved me ~6k/7k" review which is the longest in the current
// data. If reviews are swapped out later, this stays sensible.
const findFeaturedIndex = (reviews) => {
  let bestI = 0;
  let bestLen = 0;
  reviews.forEach((r, i) => {
    const len = (r?.text || '').length;
    if (len > bestLen) {
      bestLen = len;
      bestI = i;
    }
  });
  return bestI;
};

// ─── Backdrop "stage" ──────────────────────────────────────────────────

// Localized dark stage with cyan ambient glow that the glass cards blur
// against. Padded outward beyond the grid so it doesn't visually pinch
// the cards, and faded to transparent at the edges so it blends into
// the white section above/below rather than meeting them with a hard
// edge. aria-hidden because purely decorative.
const BackdropStage = () => (
  <div
    className="absolute -inset-x-4 -inset-y-8 lg:-inset-x-12 lg:-inset-y-16 pointer-events-none"
    aria-hidden="true"
  >
    {/* Soft dark radial — center of the stage darkest, fades to fully
        transparent before reaching the edges so the white section bg
        absorbs the boundary. */}
    <div
      className="absolute inset-0 rounded-[40px]"
      style={{
        background:
          'radial-gradient(ellipse 110% 100% at 50% 50%,' +
          ' rgba(10, 22, 40, 0.92) 0%,' +
          ' rgba(10, 22, 40, 0.78) 35%,' +
          ' rgba(10, 22, 40, 0.40) 65%,' +
          ' transparent 90%)',
      }}
    />
    {/* Cyan ambient — two soft blobs to give the stage subtle brand
        warmth without it becoming a light show.
        Sized 240px on mobile / 400px from sm+ up. The 400px floor was
        the root cause of horizontal scroll on iPhone SE (320px): blob
        right edge landed ~131px past the viewport right edge inside
        a stage only ~288px wide. 240px fits inside that stage with
        room to spare. Visual impact on desktop is preserved (full
        400px from 640px viewport up). */}
    <div
      className="absolute left-[12%] top-[18%] w-[240px] h-[240px] sm:w-[400px] sm:h-[400px] rounded-full"
      style={{
        background: 'radial-gradient(circle, rgba(7, 176, 233, 0.20) 0%, transparent 70%)',
        filter: 'blur(60px)',
      }}
    />
    <div
      className="absolute right-[8%] bottom-[12%] w-[240px] h-[240px] sm:w-[400px] sm:h-[400px] rounded-full"
      style={{
        background: 'radial-gradient(circle, rgba(7, 176, 233, 0.14) 0%, transparent 70%)',
        filter: 'blur(60px)',
      }}
    />
  </div>
);

// ─── Block ──────────────────────────────────────────────────────────────

const RecentReviewsBlock = () => {
  const { reviews, loading, error } = useRecentReviews(REVIEW_LIMIT);
  const showSkeletons = loading || (error && reviews.length === 0);

  // Reorder so the featured review is index 0. This makes the desktop
  // grid auto-flow place it in the top-left 2×2 block, AND on mobile
  // (single column) the featured card comes first in the stack.
  const orderedReviews = useMemo(() => {
    if (!reviews || reviews.length === 0) return reviews;
    const featuredIdx = findFeaturedIndex(reviews);
    if (featuredIdx === 0) return reviews;
    const featured = reviews[featuredIdx];
    const rest = reviews.filter((_, i) => i !== featuredIdx);
    return [featured, ...rest];
  }, [reviews]);

  if (showSkeletons) {
    return (
      <div className="relative">
        <BackdropStage />
        <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <SkeletonCard key={i} featured={i === 0} />
          ))}
        </div>
      </div>
    );
  }

  if (!orderedReviews || orderedReviews.length === 0) return null;

  return (
    <div className="relative">
      <BackdropStage />
      <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
        {orderedReviews.map((review, i) => (
          <ReviewCard
            key={review.id}
            review={review}
            index={i}
            featured={i === 0}
          />
        ))}
      </div>
    </div>
  );
};

export default RecentReviewsBlock;
